import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../lib/api';

// ─── Sprint ONBOARD ONB-FIX.D9 (#1081) — Forgot-password entry page ────────
//
// Public route /forgot-password. Submits email to POST /auth/forgot-password.
//
// ⚠️ NO-ENUMERATION CONTRACT (S1):
//   The success message shown after submit is INTENTIONALLY GENERIC and
//   identical for every email — including malformed, unknown, OAuth-only,
//   rate-limited, and known accounts. The page MUST NOT branch its UI on
//   the API response. Backend returns 200 { ok: true } for all four
//   internal states; treating any of them differently in the UI would
//   defeat the no-enumeration property that the backend works to preserve.
//
//   The ONLY non-200-equivalent state the user can reach is a Zod 400 from
//   bad email format — that's surfaced as a form validation error (caught
//   by the browser's `type="email"` before submit in normal flows).
//
// Pattern reference: LoginPage.tsx — same BlueprintMark, container shape,
// CSS-var palette, inline-style convention per match-neighbour-conventions.

function BlueprintMark({ size = 48 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="48" height="48" rx="10" fill="#7C3AED" />
      <path d="M12 32 L24 26 L36 32 L24 38 Z" fill="white" opacity="0.22" />
      <path d="M12 26 L24 20 L36 26 L24 32 Z" fill="white" opacity="0.55" />
      <path d="M12 20 L24 14 L36 20 L24 26 Z" fill="white" opacity="0.9" />
      <circle cx="36" cy="12" r="5" fill="#EC4899" />
    </svg>
  );
}

const inputStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  height: 44,
  background: 'var(--surface-alt)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  padding: '0 14px',
  fontFamily: 'var(--font-body)',
  fontSize: 14,
  color: 'var(--text)',
  outline: 'none',
  boxSizing: 'border-box',
};

export default function ForgotPasswordPage() {
  const [email, setEmail]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Fire-and-forget on the surface: we always advance to the
      // "submitted" screen regardless of network outcome. A network
      // failure that prevented even reaching the backend MIGHT warrant
      // a different UX, but distinguishing it would leak whether the
      // backend's no-enumeration path was reached at all. Treat it as
      // identical to a successful submission.
      //
      // apiFetch throws on any non-2xx; we catch and ignore so the UI
      // never branches on response status (per S1 enforcement at the
      // UI layer). R1 NIT-1: switched from raw fetch for neighbour
      // convention parity with LoginPage's apiFetch usage.
      await apiFetch('/auth/forgot-password', {
        method: 'POST',
        body:   JSON.stringify({ email }),
      }).catch(() => { /* swallow — no UI branch */ });
    } finally {
      setLoading(false);
      setSubmitted(true);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--bg)' }}>
      <div style={{
        width: '100%',
        maxWidth: 400,
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-xl)',
        boxShadow: 'var(--shadow-lg)',
        padding: 40,
      }}>
        {/* Brand header */}
        <div className="flex flex-col items-center mb-8">
          <BlueprintMark size={48} />
          <div className="flex items-baseline mt-3">
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.5px' }}>Blue</span>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--primary)', letterSpacing: '-0.5px' }}>Print</span>
          </div>
        </div>

        <h1 style={{
          fontFamily: 'var(--font-display)',
          fontSize: 20,
          fontWeight: 700,
          color: 'var(--text)',
          textAlign: 'center',
          margin: '0 0 8px',
        }}>
          {submitted ? 'Check your inbox' : 'Reset your password'}
        </h1>

        {submitted ? (
          // ⚠️ S1: generic copy that does NOT confirm whether the email
          // is registered. The same message renders regardless of which
          // internal branch the backend took (unknown_email, oauth_only,
          // rate_limited, token_issued). Worded carefully to be helpful
          // to a real user without confirming registration to a prober.
          <>
            <p
              data-testid="forgot-password-success"
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: 14,
                color: 'var(--text-secondary)',
                textAlign: 'center',
                lineHeight: 1.55,
                margin: '0 0 20px',
              }}
            >
              If an account exists for that address, we&rsquo;ve sent a password
              reset link. It will expire in 15 minutes and can only be used once.
            </p>
            <p style={{
              fontFamily: 'var(--font-body)',
              fontSize: 13,
              color: 'var(--text-secondary)',
              textAlign: 'center',
              lineHeight: 1.5,
              margin: '0 0 20px',
            }}>
              Didn&rsquo;t receive an email? Check your spam folder, or{' '}
              <button
                type="button"
                onClick={() => { setSubmitted(false); setEmail(''); }}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  color: 'var(--primary)',
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: 'inherit',
                }}
              >
                try again
              </button>.
            </p>
          </>
        ) : (
          <>
            <p style={{
              fontFamily: 'var(--font-body)',
              fontSize: 13,
              color: 'var(--text-secondary)',
              textAlign: 'center',
              margin: '0 0 24px',
              lineHeight: 1.5,
            }}>
              Enter the email associated with your account and we&rsquo;ll
              send you a link to set a new password.
            </p>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 20 }}>
                <label htmlFor="forgot-email" style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>
                  Email
                </label>
                <input
                  id="forgot-email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  style={inputStyle}
                  onFocus={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.12)'; }}
                  onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  display: 'block',
                  width: '100%',
                  height: 44,
                  background: 'var(--primary)',
                  color: 'white',
                  fontFamily: 'var(--font-display)',
                  fontSize: 15,
                  fontWeight: 600,
                  borderRadius: 'var(--radius-sm)',
                  border: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  transition: 'box-shadow 150ms',
                }}
                onMouseEnter={e => { if (!loading) e.currentTarget.style.boxShadow = 'var(--shadow-primary)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
              >
                {loading ? 'Sending…' : 'Send reset link'}
              </button>
            </form>
          </>
        )}

        <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center', marginTop: 24 }}>
          <Link
            to="/login"
            style={{ color: 'var(--primary)', fontWeight: 500, textDecoration: 'none' }}
            onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
            onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
          >
            &larr; Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
